import { Provider } from 'jotai';
import React from 'react';

import { CategoryOption } from 'api/api';

import { CategoryAddressProvider } from '~src/address/search-address/address-provider';
import { useLogPosthogEvent } from '~src/commons/services/posthog-service';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { LocationMapAlpha } from '~src/maps/location-map-alpha';

import { useHasAddress } from '~address-form/utils/address-utils';

import { CategoryExtensionProps } from '../category/category-manager';
import { useCategoryState } from '../category/category-state';
import { constructUserChoice } from '../category/user-choice';
import { UserChoiceProvider, UserChoiceProviderChildProps } from '../category/user-choice-provider';
import { LogoDeck } from '../commons/components/logo-deck-row';
import { FullscreenComponent, FullscreenMode } from '../fullscreen-internals/fullscreen-mode';
import { useSessionState } from '../global-state';
import { LocationMap } from '../maps/location-map';
import { PriceComponent } from '../price/price';
import { AriaGrid, useAriaGridCell } from '../ui/aria-grid';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { ListState, useListState } from '../ui/list-state';
import { useScrollIntoView } from '../ui/scroll-into-view';
import { Separator } from '../ui/separator';
import { ShippingCardUI } from '../ui/shipping-card';
import { DistanceComponent } from './distance';
import { InlineLocationExtension } from './inline-location-extension';
import { LocationAddress } from './location-address';
import { LocationDeliveryTime } from './location-delivery-time';
import { LocationModalLayout } from './location-modal-layout';
import { LocationOpenModalButton } from './location-open-modal-buttons';
import { LocationSearch } from './location-search';
import { LocationTypeComponent } from './location-type';
import { OperationalHoursComponent } from './operational-hours';

interface LocationListItemProps {
  option: CategoryOption;
  listState: ListState<CategoryOption>;
}

const LocationListItem: React.FC<LocationListItemProps> = ({ option, listState }) => {
  const session = useSessionState();
  const category = useCategoryState();
  const { tICU } = useTranslations();
  const logPosthogEvent = useLogPosthogEvent();

  const ref = React.useRef(null);

  const { cellProps, isSelected } = useAriaGridCell({
    itemKey: option.location!.id,
    listState,
    ref,
    enableAutoSelect: true,
  });

  useScrollIntoView(ref, isSelected);

  const location = option.location!;

  return (
    <ShippingCardUI
      disableMobileIndent
      headerExtensions={{
        price: session.flags.show_shipping_price_on_service_points_view ? (
          <PriceComponent value={option.price} />
        ) : undefined,
        logo: <LogoDeck shippingMethods={[option.carrier_logo || option.shipping_method]} />,
      }}
      radioTitle={
        isSelected ? tICU(`widget.aria.SELECTED_PICKUP_POINT`) : tICU(`widget.aria.PICKUP_POINT`)
      }
      key={location.id}
      selected={isSelected}
      spacing="dense"
      title={location.name}
      reserveLogoSpace={category.flags.show_carrier_logos}
      innerRef={ref}
      {...cellProps}
      onClick={e => {
        logPosthogEvent('Pickup Location List Item Clicked', {
          price: option.price.amount,
          name: option.location!.name,
          type: option.location!.type,
          distance_in_meters: option.location?.distance?.meter,
        });
        cellProps.onClick?.(e);
      }}
    >
      <LocationAddress address={option.location!.address} />
      <LocationDeliveryTime deliveryTime={option.delivery_times[0]!} />
      <OperationalHoursComponent enableKeyboard={isSelected} location={option.location!} />
      <Separator css={{ marginTop: '4px' }}>
        <DistanceComponent distance={location.distance} />
        <LocationTypeComponent location={location} />
      </Separator>
    </ShippingCardUI>
  );
};

interface LocationModalProps {
  initialSelectedOption: CategoryOption;
  onClose: () => void;
  onSubmit: (option: CategoryOption) => void;
}

export const LocationModal: FullscreenComponent<LocationModalProps> = ({
  initialSelectedOption,
  onClose,
  onSubmit,
}) => {
  const session = useSessionState();
  const category = useCategoryState();

  const options = category.options.filter(option => !!option.location);

  const defaultOption = !!initialSelectedOption.location ? initialSelectedOption : options[0];

  const listState = useListState({
    items: options,
    getKey: (option: CategoryOption) => option.location!.id,
    defaultSelectedKey: defaultOption.location!.id,
  });

  const showMap = session.flags.show_map;
  const showSearch = category.instore_search?.enabled;

  const { tICU } = useTranslations();

  const title =
    category.delivery_type === 'instore'
      ? tICU('widget.SHIPPING_OPTIONS_MODAL.instore.TITLE')
      : tICU('widget.SELECT_SERVICE_POINT');

  const mapElement = session.flags.enable_maptiler ? (
    <LocationMapAlpha listState={listState} />
  ) : (
    <LocationMap listState={listState} />
  );

  const searchElement = <LocationSearch listState={listState} />;

  const listElement = (
    <AriaGrid listState={listState}>
      {(option: CategoryOption) => (
        <LocationListItem key={option.location!.id} option={option} listState={listState} />
      )}
    </AriaGrid>
  );

  return (
    <Dialog height={680} width={showMap ? 780 : 520} onClose={onClose}>
      <LocationModalLayout
        title={title}
        mapElement={showMap ? mapElement : undefined}
        searchElement={showSearch ? searchElement : undefined}
        listElement={listElement}
        buttonElement={
          <Button
            disabled={!listState.getSelectedItem()}
            type="submit"
            onClick={() => onSubmit(listState.getSelectedItem()!)}
          >
            {tICU('widget.SELECT_BUTTON')}
          </Button>
        }
        onClose={onClose}
      />
    </Dialog>
  );
};
LocationModal.fullscreenName = 'LocationModal';

export const LocationExtension: React.FC<CategoryExtensionProps> = ({
  selected,
  selectedOption,
}) => {
  const category = useCategoryState();
  const hasAddress = useHasAddress();
  const session = useSessionState();
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const logPosthogEvent = useLogPosthogEvent();

  if (
    !selected ||
    (category.delivery_type !== 'pickup' && category.delivery_type !== 'instore') ||
    !category.flags.enable_choice_selection ||
    /**
     * Backend can return empty location for invalid zip code if the
     * "fail_on_no_options" flag is not set.
     *
     * For security disable this extension if any option has empty location.
     * Handling this would be very complicated.
     */
    (hasAddress && category.options.some(option => !option.location))
  ) {
    return null;
  }

  if (session.flags.enable_inline_category_extensions) {
    return (
      <Provider>
        <InlineLocationExtension initialSelectedOption={selectedOption} />
      </Provider>
    );
  }

  const renderModal = ({ onUpdate }: UserChoiceProviderChildProps) => {
    if (!modalOpen) {
      return null;
    }

    return (
      <FullscreenMode>
        <LocationModal
          initialSelectedOption={selectedOption}
          onClose={() => {
            setModalOpen(false);
            logPosthogEvent('Pickup Location Modal Canceled');
          }}
          onSubmit={(newOption: CategoryOption) => {
            logPosthogEvent('Pickup Location Changed', {
              price: newOption.price.amount,
              name: newOption.location!.name,
              type: newOption.location!.type,
              distance_in_meters: newOption.location?.distance?.meter,
            });
            onUpdate(constructUserChoice(category, newOption));
            setModalOpen(false);
          }}
        />
      </FullscreenMode>
    );
  };

  const location = selectedOption.location;

  return (
    <>
      <CategoryAddressProvider onUpdateAddress={() => setModalOpen(true)}>
        {({ onOpen }) => (
          <LocationOpenModalButton
            location={location}
            onOpen={
              hasAddress
                ? () => {
                    logPosthogEvent('Pickup Location Modal Viewed');
                    setModalOpen(true);
                  }
                : () => {
                    onOpen();
                    logPosthogEvent('Address Required For Pickup Location Modal Viewed');
                  }
            }
          />
        )}
      </CategoryAddressProvider>

      <UserChoiceProvider>{renderModal}</UserChoiceProvider>
    </>
  );
};
